/**
 * SAPSAN Custom Search v7 — Blueprint style
 * Security: XSS-safe, no innerHTML with user data
 * Performance: lazy init — only activates on first search interaction
 * Features: bestsellers hydrated on first focus, vendor filter
 */
(function() {
  'use strict';

  // === FETCH/XHR INTERCEPT (must be early — lightweight, just URL check) ===
  function isSearchUrl(url) {
    return url.indexOf('/search/suggest') !== -1 || url.indexOf('predictive-search') !== -1;
  }

  function patchSearchUrl(url) {
    try {
      var urlObj = new URL(url, window.location.origin);
      var changed = false;
      // SAPSAN: Empire requests products only. Also ask the predictive-search
      // section for matching collections, to render category suggestion chips.
      if (urlObj.searchParams.get('section_id') === 'predictive-search' &&
          !urlObj.searchParams.has('resources[type]')) {
        urlObj.searchParams.set('resources[type]', 'product,collection,article');
        urlObj.searchParams.set('resources[limit]', '10');
        urlObj.searchParams.set('resources[limit_scope]', 'each');
        changed = true;
      }
      if (changed) return urlObj.toString();
    } catch(e) {}
    return url;
  }

  // === SPEED LAYER (2026-06-12) ===
  // Empire's live search fires a fetch on EVERY keystroke, clears results
  // before each request and never cancels stale ones (race: old responses
  // can overwrite newer). All fixed here, at the fetch layer Empire uses:
  //  - 180ms debounce (typing fast = one request at the pause)
  //  - session cache (repeat queries render instantly)
  //  - supersede: a stale in-flight request never resolves, so it can
  //    neither race nor flash old results (GC reclaims the hung promise)
  //  - loading class on <html> drives the CSS shimmer/skeleton
  var searchCache = new Map();
  var SEARCH_CACHE_MAX = 40;
  var searchSeq = 0;
  var searchDebounce = null;

  function setSearchLoading(on) {
    document.documentElement.classList.toggle('sapsan-search-loading', !!on);
  }

  function cachedResponse(html) {
    return new Response(html, { status: 200, headers: { 'Content-Type': 'text/html' } });
  }

  function sapsanSearchFetch(url, options) {
    var hit = searchCache.get(url);
    if (hit !== undefined) {
      searchSeq++;               // supersede anything in flight
      setSearchLoading(false);
      return Promise.resolve(cachedResponse(hit));
    }
    var mySeq = ++searchSeq;
    setSearchLoading(true);
    return new Promise(function(resolve) {
      if (searchDebounce) clearTimeout(searchDebounce);
      searchDebounce = setTimeout(function() {
        if (mySeq !== searchSeq) return;   // superseded while debouncing
        originalFetch.call(window, url, options).then(function(res) {
          if (!res.ok) {
            if (mySeq === searchSeq) { setSearchLoading(false); resolve(res); }
            return;
          }
          // read body, cache it, THEN resolve - so an identical request that
          // fires right after (backspace/retype) gets a synchronous cache hit
          return res.text().then(function(t) {
            if (searchCache.size >= SEARCH_CACHE_MAX) {
              searchCache.delete(searchCache.keys().next().value);
            }
            searchCache.set(url, t);
            if (mySeq === searchSeq) { setSearchLoading(false); resolve(cachedResponse(t)); }
            // superseded: hang silently - Empire never renders the stale HTML
          });
        }).catch(function() {
          if (mySeq === searchSeq) {
            setSearchLoading(false);
            resolve(cachedResponse(''));   // benign empty render, no JS error
          }
        });
      }, 180);
    });
  }

  var originalFetch = window.fetch;
  window.fetch = function(url, options) {
    if (typeof url === 'string' && isSearchUrl(url)) {
      return sapsanSearchFetch(patchSearchUrl(url), options);
    }
    return originalFetch.call(this, url, options);
  };

  var originalXhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function(method, url) {
    if (typeof url === 'string' && isSearchUrl(url)) {
      arguments[1] = patchSearchUrl(url);
    }
    return originalXhrOpen.apply(this, arguments);
  };

  // === LAZY INIT — everything below runs ONLY on first search interaction ===
  var initialized = false;
  var overlay = null;

  function hydrateBestsellers(container) {
    var dataEl = container.querySelector('[data-sapsan-bestsellers-data]');
    var list = container.querySelector('[data-sapsan-bestsellers-list]');
    if (!dataEl || !list || list.dataset.sapsanHydrated === '1') return;
    var products;
    try {
      products = JSON.parse(dataEl.textContent);
    } catch (error) {
      products = [];
    }
    products.forEach(function(product) {
      var item = document.createElement('li');
      item.className = 'search-flydown--quicklinks-item';
      item.setAttribute('data-vendor', product.vendor || '');
      var link = document.createElement('a');
      link.className = 'sapsan-search__bestseller-item';
      link.href = product.url;
      if (product.image) {
        var imageBox = document.createElement('div');
        imageBox.className = 'sapsan-search__bestseller-image';
        var image = document.createElement('img');
        image.src = product.image;
        image.alt = product.title || '';
        image.width = 48;
        image.height = 48;
        image.loading = 'lazy';
        imageBox.appendChild(image);
        link.appendChild(imageBox);
      }
      var title = document.createElement('span');
      title.className = 'sapsan-search__bestseller-title';
      title.textContent = product.title || '';
      link.appendChild(title);
      item.appendChild(link);
      list.appendChild(item);
    });
    list.dataset.sapsanHydrated = '1';
    dataEl.remove();
  }

  function filterVendors() {
    var lang = document.documentElement.lang || 'pl';
    if (lang.indexOf('pl') === 0) return;
    var items = document.querySelectorAll('[data-live-search-quick-links] [data-vendor]');
    for (var i = 0; i < items.length; i++) {
      if (items[i].getAttribute('data-vendor') === 'Dawid Kuciel') {
        items[i].style.display = 'none';
      }
    }
  }

  function showOverlay() {
    if (overlay) return;
    overlay = document.createElement('div');
    overlay.className = 'sapsan-search-overlay';
    overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.35);z-index:1000;';
    overlay.addEventListener('click', function() {
      var input = document.querySelector('[data-live-search-input]');
      if (input) input.blur();
      hideOverlay();
      var containers = document.querySelectorAll('[data-live-search]');
      for (var i = 0; i < containers.length; i++) {
        hideBestsellers(containers[i]);
      }
    });
    document.body.appendChild(overlay);
  }

  function hideOverlay() {
    if (overlay) {
      overlay.remove();
      overlay = null;
    }
  }

  function showBestsellers(container) {
    var flydown = container.querySelector('[data-live-search-flydown]');
    if (!flydown) return;

    var resultsEl = flydown.querySelector('[data-live-search-results]');
    var placeholder = flydown.querySelector('[data-live-search-placeholder]');
    var quickLinks = flydown.querySelector('[data-live-search-quick-links]');

    if (placeholder) placeholder.style.display = 'none';
    if (resultsEl) resultsEl.style.display = 'none';
    if (quickLinks) quickLinks.style.display = '';

    flydown.style.display = 'block';
    flydown.style.visibility = 'visible';
    flydown.style.opacity = '1';
    container.classList.add('live-search--active');

    if (window.innerWidth < 720) showOverlay();
  }

  function hideBestsellers(container) {
    var flydown = container.querySelector('[data-live-search-flydown]');
    if (flydown) {
      flydown.style.display = '';
      flydown.style.visibility = '';
      flydown.style.opacity = '';
    }
    container.classList.remove('live-search--active');
  }

  function initSearchUI() {
    if (initialized) return;
    initialized = true;

    document.querySelectorAll('[data-live-search]').forEach(hydrateBestsellers);
    filterVendors();

    // Watch for Empire closing takeover → remove overlay
    // Targeted: only observe body class changes
    new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (!document.body.classList.contains('mobile-search-takeover-active') && overlay) {
          hideOverlay();
          break;
        }
      }
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

    // Input changes — hide bestsellers when typing
    document.addEventListener('input', function(e) {
      if (!e.target.matches || !e.target.matches('[data-live-search-input]')) return;
      var container = e.target.closest('[data-live-search]');
      if (!container) return;

      if (e.target.value.trim() === '') {
        showBestsellers(container);
      } else {
        var quickLinks = container.querySelector('[data-live-search-quick-links]');
        if (quickLinks) quickLinks.style.display = 'none';
        var resultsEl = container.querySelector('[data-live-search-results]');
        if (resultsEl) resultsEl.style.display = '';
      }
    });

    // Click outside → cleanup
    document.addEventListener('click', function(e) {
      var containers = document.querySelectorAll('[data-live-search]');
      for (var i = 0; i < containers.length; i++) {
        if (!containers[i].contains(e.target)) {
          hideBestsellers(containers[i]);
          hideOverlay();
        }
      }
    });
  }

  // === SAPSAN 07.10.2026: lista bestsellerów musi istnieć ZANIM Empire zmierzy
  // flydown przy pierwszym fokusie (_openFlyDown bierze scrollHeight w onStart).
  // Hydracja dopiero na focusin dawała ucięty dropdown (91 px zamiast ~620 px),
  // naprawiany dopiero przy kolejnym otwarciu.
  function prepareBestsellers() {
    document.querySelectorAll('[data-live-search]').forEach(hydrateBestsellers);
    filterVendors();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', prepareBestsellers);
  } else {
    prepareBestsellers();
  }

  // === SINGLE ENTRY POINT: focusin on search input ===
  document.addEventListener('focusin', function(e) {
    if (!e.target || !e.target.matches || !e.target.matches('[data-live-search-input]')) return;

    // Lazy init on first interaction
    initSearchUI();

    // Show bestsellers on empty focus
    if (e.target.value.trim() === '') {
      var container = e.target.closest('[data-live-search]');
      if (container) showBestsellers(container);
    }
  });

  // === SAPSAN: kategorie-chipsy na stronie wyników (/search) ===
  // Reużywa sekcję predictive-search (przez fetch + interceptor dodaje collection),
  // wyciąga te same chipsy co w dropdownie (synonimy + auto-match + dedup) i wstawia
  // pod nagłówkiem wyników. Tylko nawigacja - artykuły idą server-side (SEO).
  function initResultsChips() {
    if (!document.body || document.body.className.indexOf('template-search') === -1) return;
    if (document.querySelector('.sapsan-results-chips')) return;
    var head = document.querySelector('.sapsan-results-head');
    var anchor = head || document.querySelector('.productgrid--masthead');
    if (!anchor) return;
    var q = new URLSearchParams(window.location.search).get('q');
    if (!q || !window.Theme || !window.Theme.routes) return;

    var url = window.Theme.routes.predictive_search_url +
              '?q=' + encodeURIComponent(q) + '&section_id=predictive-search';
    fetch(url).then(function(r){ return r.ok ? r.text() : ''; }).then(function(html){
      if (!html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var chips = doc.querySelectorAll('.sapsan-search__chip');
      if (!chips.length) return;

      var box = document.createElement('div');
      box.className = 'sapsan-results-chips';

      var lang = (document.documentElement.lang || 'pl').slice(0, 2);
      var labels = { pl: 'Kategorie', en: 'Categories', de: 'Kategorien', ru: 'Категории', cs: 'Kategorie' };
      var lbl = document.createElement('span');
      lbl.className = 'sapsan-results-chips__label';
      lbl.textContent = labels[lang] || labels.pl;
      box.appendChild(lbl);

      var list = document.createElement('div');
      list.className = 'sapsan-results-chips__list';
      for (var i = 0; i < chips.length; i++) {
        list.appendChild(chips[i].cloneNode(true));
      }
      box.appendChild(list);
      anchor.insertAdjacentElement('afterend', box);
    }).catch(function(){});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initResultsChips);
  } else {
    initResultsChips();
  }

})();



